import React, { useState, useEffect, ReactNode, MutableRefObject, useMemo } from 'react';
import useBundleTranslation from 'i18n';
import { Button, Box, Typography, LinearProgress } from '@mui/material';
import { formAPI } from 'api/form';
import IconMi from 'components/common/icon/IconMi';
import { Popup } from 'components/common/popup/Popup';
import { FormPanelButtonProps } from 'components/common/form';
import { FormControlProps } from 'components/common/form/layout/control';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/store';
import { setIndexUpdateProgressPopupOpen } from 'store/formSlice';

export interface ProgressResponse {
    status: string;
    data: any;
}

export interface FormPanelButtonUpdateIndexesControlProps extends FormControlProps {
    getIndexStatusUrl: string;
    updateIndexUrl: string;
    cancelIndexUpdateUrl: string;
    alert_flag_component_name: string;
    isLink?: boolean;
    noForceCheckbox?: boolean;
}

interface FormPanelButtonUpdateIndexesBaseProps extends FormPanelButtonProps<FormPanelButtonUpdateIndexesControlProps> {
    getInitialProgressStatus: (res: ProgressResponse) => boolean;
    getProgressValue: (res: ProgressResponse) => number | null;
    confirmationPopupContent: ReactNode;
    confirmationPopupTitle?: string;
    triggerRef?: MutableRefObject<any>;
    checkDelay?: number;
    extraUrlParams?: { [key: string]: string };
    onConfirmHide?: () => void;
}

export default function FormPanelButtonUpdateIndexesBase({
    button,
    formActions,
    getInitialProgressStatus,
    getProgressValue,
    confirmationPopupContent,
    confirmationPopupTitle,
    triggerRef,
    checkDelay = 3000,
    extraUrlParams,
    onConfirmHide,
}: FormPanelButtonUpdateIndexesBaseProps) {
    const dispatch = useDispatch();
    const globalProgressPopupOpen = useSelector((state: RootState) => state.form.indexUpdateProgressPopupOpen);

    const [progress, setProgress] = useState(0);
    const [progressPopupOpen, toggleProgressPopupOpen] = useState(false);
    const [cancelDisabled, setCancelDisabled] = useState(false);
    const [updatingIndexStarted, toggleUpdatingIndexStarted] = useState(false);
    const [confirmationPopupOpen, toggleConfirmationPopupOpen] = useState(false);
    const { t } = useBundleTranslation([button.translationNS ?? 'components/common/form/form']);

    const simpleForm = useCustomSimplifiedForm({ force: 'N' });
    const isForce = useMemo(() => {
        return extraUrlParams?.force == '1' || false;
    }, []);

    const noForceCheckbox = button.props.noForceCheckbox == true;
    const getIndexStatusUrl = button.props.getIndexStatusUrl;
    const cancelIndexUpdateUrl = button.props.cancelIndexUpdateUrl;
    const updateIndexUrl = button.props.updateIndexUrl;
    const isLink = button.props.isLink ?? false;
    const confVar: string = useConfigConstantValue('GLOBAL_SEARCH_DATA_ANALYZER_INDEX_TYPE');

    const onConfirmPopupHide = () => {
        toggleConfirmationPopupOpen(false);
        if (onConfirmHide) {
            onConfirmHide();
        }
    };

    const checkOnButtonInit = async () => {
        const response = await formAPI.load(getIndexStatusUrl);
        if (response.data.status == 'OK' && getInitialProgressStatus(response.data)) {
            const currentProgress = getProgressValue(response.data);
            toggleUpdatingIndexStarted(true);
            if (!globalProgressPopupOpen) {
                toggleProgressPopupOpen(true);
                dispatch(setIndexUpdateProgressPopupOpen(true));
            }
            if (currentProgress !== null) {
                setProgress(currentProgress);
            }
        }
    };

    useEffect(() => {
        checkOnButtonInit();
    }, []);

    const initIndexUpdate = async () => {
        if (!globalProgressPopupOpen) {
            toggleProgressPopupOpen(true);
            dispatch(setIndexUpdateProgressPopupOpen(true));
        }
        setProgress(0);

        const data = structuredClone(extraUrlParams) ?? {};
        if (simpleForm.form.hookFormGetValues('force') == 'Y') {
            data.force = '1';
        }

        const response = await formAPI.load(updateIndexUrl, data);
        if (response.data.status == 'OK') {
            startIndexUpdate();
        }
    };

    const getIndexStatus = async () => {
        if (!updatingIndexStarted) return;

        const response = await formAPI.load(getIndexStatusUrl);
        if (response.data.status === 'OK') {
            const value = getProgressValue(response.data);
            setCancelDisabled(false);
            if (value !== null) {
                setProgress(value);
                if (value >= 100) {
                    toggleUpdatingIndexStarted(false);
                    if (progressPopupOpen) {
                        dispatch(setIndexUpdateProgressPopupOpen(false));
                    }
                }
            }

            if (value === null) {
                toggleUpdatingIndexStarted(false);
                setProgress(100);
                if (progressPopupOpen) {
                    dispatch(setIndexUpdateProgressPopupOpen(false));
                }
            }
        }
    };

    useEffect(() => {
        const timer = updatingIndexStarted ? setInterval(getIndexStatus, checkDelay) : null;
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [updatingIndexStarted]);

    useEffect(() => {
        return () => {
            if (progressPopupOpen) {
                dispatch(setIndexUpdateProgressPopupOpen(false));
            }
        };
    }, [progressPopupOpen, dispatch]);

    const startIndexUpdate = () => {
        const { alert_flag_component_name } = button.props;
        if (alert_flag_component_name) {
            formActions.hookFormSetValue('dataset_grid_init_value', formActions.hookFormGetValues('dataset_grid'));
            formActions.hookFormSetValue('reference_grid_init_value', formActions.hookFormGetValues('reference_grid'));
            formActions.hookFormSetValue('grid_was_changed', '');
            setTimeout(() => {
                if (Array.isArray(alert_flag_component_name)) {
                    alert_flag_component_name.forEach((f) => formActions.hookFormSetValue(f, ''));
                } else {
                    formActions.hookFormSetValue(alert_flag_component_name, '');
                }
            }, 0);
        }
        toggleUpdatingIndexStarted(true);
        setCancelDisabled(true);
        onConfirmPopupHide();
    };

    const [checkBoxLabels, setCheckBoxLabels] = useState('');
    useEffect(() => {
        // Main search page
        if (window.location.pathname == '/admin/search' || window.location.pathname == '/admin/search/') {
            setCheckBoxLabels('_main');
        }
    }, [window.location.pathname]);

    return (
        <>
            {isLink ? (
                <span
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    ref={triggerRef}
                    onClick={() => {
                        if (updatingIndexStarted) {
                            return;
                        }
                        toggleConfirmationPopupOpen(true);
                    }}
                >
                    {t(button.label)}
                </span>
            ) : (
                <Button
                    id={'update_indexes_button'}
                    ref={triggerRef}
                    disabled={updatingIndexStarted}
                    onClick={() => toggleConfirmationPopupOpen(true)}
                    sx={{ marginLeft: '12px' }}
                    startIcon={updatingIndexStarted ? null : <IconMi icon="arrows-left-and-right" />}
                    variant="outlined"
                >
                    {t(button.label)}
                </Button>
            )}

            <Popup
                maxWidth="popupMd"
                onConfirm={initIndexUpdate}
                open={confirmationPopupOpen}
                settings={{
                    title: confirmationPopupTitle ?? t('proceed_action'),
                    textOK: t('proceed'),
                }}
                onHide={onConfirmPopupHide}
            >
                <Box>{confirmationPopupContent}</Box>
                {confVar != 'full' && !isForce && !noForceCheckbox && (
                    <Box sx={{ mt: 1 }}>
                        <TemplateFormComponent
                            component={'FormSwitch'}
                            elementProps={simpleForm}
                            name={'force'}
                            label={'update_index_confirmation.optimized.checkbox' + checkBoxLabels}
                            props={{
                                tooltip: {
                                    icon: t(
                                        'update_index_confirmation.optimized.checkbox_description' + checkBoxLabels,
                                    ),
                                },
                            }}
                        />
                    </Box>
                )}
            </Popup>

            <Popup
                maxWidth="popupSm"
                open={progressPopupOpen}
                okDisabled={progress !== 100}
                settings={{
                    title: t('indexing_in_progress'),
                    textOK: t('done'),
                    cancelDisabled: cancelDisabled,
                }}
                onHide={() => {
                    toggleProgressPopupOpen(false);
                    dispatch(setIndexUpdateProgressPopupOpen(false));
                }}
                onConfirm={() => {
                    toggleProgressPopupOpen(false);
                    dispatch(setIndexUpdateProgressPopupOpen(false));
                    toggleUpdatingIndexStarted(false);
                    setProgress(0);
                }}
                onCancel={async () => {
                    toggleProgressPopupOpen(false);
                    dispatch(setIndexUpdateProgressPopupOpen(false));
                    toggleUpdatingIndexStarted(false);
                    await formAPI.load(cancelIndexUpdateUrl);
                    setProgress(0);
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <LinearProgress sx={{ height: '25px' }} variant="determinate" value={progress} />
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'background.default',
                            width: `${progress <= 6 ? 6 : progress}%`,
                        }}
                    >
                        <Typography>{progress.toFixed(0)}%</Typography>
                    </Box>
                </Box>
            </Popup>
        </>
    );
}
